// The change guard of a markup write (ui-map-interface.md §10.3): an element
// takes new markup only when the markup changed, so a 4 Hz pass over a
// panel that says nothing new writes nothing to the DOM.
//
// One host holds one element and the markup it wrote last. A write costs
// one string compare when nothing changed, and it allocates nothing: the
// focus keep below runs a writer that the host made once.

import { keepFocus } from './pressroute';

/** One element and the markup it took last. */
export class MarkupHost {
  private last = '';

  constructor(readonly el: HTMLElement) {}

  /** The markup of the last write, or '' before the first write and after
   *  `forget` or `clear`. */
  get html(): string { return this.last; }

  /**
   * Writes `html` into the element when it differs from the last write, or
   * always with `force`. True when it wrote, so the caller can do the work
   * that belongs to new markup.
   */
  write(html: string, force = false): boolean {
    if (html === this.last && !force) return false;
    this.last = html;
    this.paint();
    return true;
  }

  /** Forgets the last write: the next write of markup that is not empty
   *  writes. The element keeps what it holds. */
  forget(): void {
    this.last = '';
  }

  /** Empties the element and forgets the last write. */
  clear(): void {
    this.last = '';
    this.el.innerHTML = '';
  }

  /** Records markup that the caller wrote into the element itself. */
  seed(html: string): void {
    this.last = html;
  }

  /** Puts the last markup into the element. */
  protected paint(): void {
    this.el.innerHTML = this.last;
  }
}

/** The options of `keepFocus`. */
type FocusOptions = NonNullable<Parameters<typeof keepFocus>[2]>;

/**
 * A host whose write keeps the keyboard focus (R1698): a control that the
 * keyboard focused takes the focus back after the rewrite, so an armed
 * control takes its second key press.
 */
export class FocusMarkupHost extends MarkupHost {
  private readonly put = (): void => { this.el.innerHTML = this.html; };

  constructor(el: HTMLElement, private readonly opts: FocusOptions = {}) {
    super(el);
  }

  protected override paint(): void {
    keepFocus(this.el, this.put, this.opts);
  }
}
